import { Link } from "react-router-dom";
import { useContent } from "../../context/ContentContext";
import { Reveal } from "./Reveal";
import { CalendarDays, MapPin, ArrowRight, Play } from "lucide-react";
import { galleryCover, mediaItem, mediaItems } from "../../lib/media";
import { FramedImage } from "./FramedImage";
import { isVisible, isLocked, isPast, pastBadgeLabel, temporalBadge, formationDurationLabel } from "../../lib/publish";
import { CardMediaCarousel } from "./SmartGallery";

export const publishedFormations = (fm) =>
  (fm?.items || [])
    .filter((it) => isVisible(it))
    .map((it, i) => ({ ...it, _i: i }))
    .sort((a, b) => (a.order ?? a._i) - (b.order ?? b._i));

export const sessionRemaining = (s) => Math.max(0, (Number(s.totalSpots) || 0) - (Number(s.takenSpots) || 0));

export const formationRemaining = (it) => (it.sessions || []).reduce((sum, s) => sum + sessionRemaining(s), 0);

export const FormationCard = ({ it, fm, i = 0, contactHref = "#contact" }) => {
  const rem = formationRemaining(it);
  const isFull = (it.sessions || []).length > 0 && rem === 0;
  const locked = isLocked(it);
  const past = isPast(it);
  const src = it.mediaType === "slideshow" ? galleryCover(it.gallery) : it.image;
  const fmedia = it.mediaType === "slideshow" ? mediaItem((it.gallery || [])[0]) : null;
  const isVideoCover = it.mediaType === "video" || (fmedia && fmedia.type === "video");
  const multiMedia = it.mediaType === "slideshow" && mediaItems(it.gallery).length > 1;
  const href = `/formation/${it.slug || it.id}`;
  const temporal = temporalBadge(it);
  const durationLabel = formationDurationLabel(it);
  return (
    <Reveal delay={i * 0.08}>
      <Link to={href} data-testid={`formation-card-${i}`} className="group rounded-lg overflow-hidden border border-white/10 bg-surface/60 hover:border-electric/40 transition-colors duration-500 h-full flex flex-col">
        <div className={`relative h-52 overflow-hidden bg-[#0e0e0e] ${past ? "grayscale-[0.35]" : ""}`}>
          {multiMedia
            ? <CardMediaCarousel images={it.gallery} settings={it.gallerySettings} />
            : <>
                {src ? <div className="w-full h-full transition-transform duration-[1.2s] group-hover:scale-110"><FramedImage src={src} frame={it.imageFrame} alt={it.title} /></div> : <div className="w-full h-full" />}
                {isVideoCover && <span className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="h-12 w-12 rounded-full bg-black/50 border border-white/40 flex items-center justify-center text-white"><Play size={18} className="ml-0.5" /></span></span>}
              </>}
          <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
            {past
              ? <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border backdrop-blur text-purple-200 border-purple-400/50 bg-black/50" data-testid={`formation-past-${i}`}>{pastBadgeLabel(it)}</span>
              : locked
              ? <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border backdrop-blur text-amber-400 border-amber-400/50 bg-black/50" data-testid={`formation-coming-${i}`}>{fm.comingLabel || "Bientôt disponible"}</span>
              : (it.sessions || []).length > 0 && (
              <span className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border backdrop-blur ${isFull ? "text-red-400 border-red-400/50 bg-black/50" : "text-electric border-electric/50 bg-black/50"}`} data-testid={`formation-status-${i}`}>
                {isFull ? (fm.fullLabel || "Complet") : `${rem} ${fm.spotsSuffix || "places restantes"}`}
              </span>
            )}
            {temporal && temporal.label !== "À venir" && temporal.label !== (isFull ? (fm.fullLabel || "Complet") : "") && (
              <span className={`text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border backdrop-blur bg-black/50 ${temporal.tone}`} data-testid={`formation-temporal-${i}`}>{temporal.label}</span>
            )}
          </div>
          {(it.level || durationLabel || it.price) && (
            <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
              {[it.level, durationLabel, it.price].filter(Boolean).map((t, x) => (
                <span key={x} className="text-[10px] tracking-[0.15em] uppercase text-white/90 bg-black/50 backdrop-blur px-2.5 py-1 rounded-full border border-white/15">{t}</span>
              ))}
            </div>
          )}
        </div>
        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-serif text-2xl text-white mb-3">{it.title}</h3>
          <p className="text-sm text-secondary2 font-light leading-relaxed mb-5">{it.description}</p>

          {(it.sessions || []).length > 0 && (
            <div className="space-y-2 mb-6">
              {it.sessions.map((s, si) => {
                const r = sessionRemaining(s);
                return (
                  <div key={s.id || si} className="flex items-center justify-between gap-3 text-xs border-t border-white/10 pt-2.5">
                    <span className="inline-flex items-center gap-3 text-white/80">
                      <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} className="text-electric" /> {s.date}{s.time ? ` · ${s.time}` : ""}</span>
                      {s.location && <span className="inline-flex items-center gap-1.5 text-secondary2"><MapPin size={13} /> {s.location}</span>}
                    </span>
                    <span className={r === 0 ? "text-red-400" : "text-electric"}>{r === 0 ? (fm.fullLabel || "Complet") : `${r}`}</span>
                  </div>
                );
              })}
            </div>
          )}

          <span data-testid={`formation-cta-${i}`}
            className={`mt-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold transition-colors ${past || locked || isFull ? "bg-white/5 text-white/40 border border-white/10" : "bg-electric text-black group-hover:bg-white"}`}>
            {past ? "Terminé" : locked ? (fm.comingLabel || "Bientôt disponible") : isFull ? (fm.fullLabel || "Complet") : (fm.ctaLabel || "S'inscrire")} {!past && !locked && !isFull && <ArrowRight size={15} />}
          </span>
        </div>
      </Link>
    </Reveal>
  );
};

export const Formations = () => {
  const { content } = useContent();
  const fm = content.formations;
  const items = publishedFormations(fm);
  if (!fm || !items.length) return null;
  return (
    <section id="formations" className="bg-[#050505] py-24 md:py-40 border-t border-white/10" data-testid="formations-section">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal className="mb-16 md:mb-20 max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-electric" />
            <span className="text-xs tracking-[0.35em] uppercase text-electric">{fm.eyebrow}</span>
          </div>
          <h2 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-6">{fm.heading}</h2>
          {fm.intro && <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed">{fm.intro}</p>}
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0, 3).map((it, i) => <FormationCard key={it.id || i} it={it} fm={fm} i={i} />)}
        </div>

        {items.length > 3 && (
          <div className="mt-12">
            <Link to="/formations" data-testid="formations-see-all" className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-white/10 text-white text-[12px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors">
              Toutes les formations <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
